import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Grid, IconButton, Tooltip, Button, TextField } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { OrgContext } from '../contexts/OrgContext'; // Added to get orgId
import { getProductById, updateProduct } from '../services/productService'; // Use the actual API
import BackButton from '../components/BackButton';

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState({});
  const { orgId } = useContext(OrgContext); // Get orgId here

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const result = await getProductById(orgId, productId);
        // Exclude meta and date_modified
        const { meta, date_modified, organization, ...rest } = result;
        const { meta: orgMeta, date_modified: orgDateModified, ...orgRest } = organization || {};
        setProduct({ ...rest, organization: orgRest });
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    if (orgId && productId) {
      fetchProduct();
    }
  }, [orgId, productId]);

  useEffect(() => {
    if (product) {
      setEditableData({
        product_name: product.product_name,
        category: product.category,
        sub_category: product.sub_category,
        unit_of_measure: product.unit_of_measure,
        company: product.company, // Changed from merchant to company
        scanner_reference: product.scanner_reference,
        sku: product.sku,
        status: product.status,
      });
    }
  }, [product]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateProduct(orgId, productId, editableData); // Call updateProduct API
      setIsEditing(false);
      setProduct({ ...product, ...editableData }); // Update local state
    } catch (error) {
      console.error(error);
    }
  };

  // Define main fields
  const mainFields = product ? {
    'Product ID': product.id,
    'Product Name': product.product_name,
    'Category': product.category,
    'Sub Category': product.sub_category,
    'Type of Inventory': product.type_of_inventory,
    'Unit of Measure': product.unit_of_measure,
    'Company': product.company, // Changed from "Merchant" to "Company"
    'SKU': product.sku,
    'Scanner Reference': product.scanner_reference,
    'Organization ID': product.organization?.id,
    'Organization Name': product.organization?.organization_name,
  } : {};

  return (
    <Box sx={{ p: 4 }}>
      <BackButton />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        product && (
      <Paper sx={{ p: 3, bgcolor: 'background.paper', color: 'text.primary', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h5" gutterBottom>
              {product.product_name}
            </Typography>

            {!isEditing ? (
        <Button variant="contained" onClick={handleEdit} sx={{ mb: 2 }}>
                Edit Product
              </Button>
            ) : (
        <Button variant="contained" onClick={handleSave} sx={{ mb: 2 }}>
                Save
              </Button>
            )}

            {isEditing ? (
              // Render editable fields
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Product Name"
                    value={editableData.product_name || ''}
                    onChange={(e) => setEditableData({ ...editableData, product_name: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Category"
                    value={editableData.category || ''}
                    onChange={(e) => setEditableData({ ...editableData, category: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Sub Category"
                    value={editableData.sub_category || ''}
                    onChange={(e) => setEditableData({ ...editableData, sub_category: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Unit of Measure"
                    value={editableData.unit_of_measure || ''}
                    onChange={(e) => setEditableData({ ...editableData, unit_of_measure: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Company"
                    value={editableData.company || ''}
                    onChange={(e) => setEditableData({ ...editableData, company: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Scanner Reference"
                    value={editableData.scanner_reference || ''}
                    onChange={(e) => setEditableData({ ...editableData, scanner_reference: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="SKU"
                    value={editableData.sku || ''}
                    onChange={(e) => setEditableData({ ...editableData, sku: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            ) : (
              // Existing read-only fields
              <Grid container spacing={2}>
                {Object.entries(mainFields).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {key}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={5} md={7}>
                      <Typography variant="body1">{value}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={1} md={1} display="flex" alignItems="center" justifyContent="flex-end">
                      <Tooltip title="Copy">
                        <IconButton onClick={() => handleCopy(value)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            )}
          </Paper>
        )
      )}
    </Box>
  );
};

export default ProductDetail;
